import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  ArrowLeft, Loader2, CheckCircle, AlertTriangle, CalendarDays, Users, Percent, Phone, MessageSquare, Mail,
  BellRing, CalendarClock, UserX,
} from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '@/config/api';
import { useCelebration } from '@/hooks/useCelebration';
import { StatStrip } from '@/components/training/KanbanBoard';
import MessageDialog from '@/components/training/MessageDialog';

const STATUS = {
  present: { label: 'Present', short: 'P', on: 'bg-green-500 text-white', cell: 'bg-green-100 text-green-700' },
  late: { label: 'Late', short: 'L', on: 'bg-amber-500 text-white', cell: 'bg-amber-100 text-amber-700' },
  absent: { label: 'Absent', short: 'A', on: 'bg-red-500 text-white', cell: 'bg-red-100 text-red-700' },
  excused: { label: 'Excused', short: 'E', on: 'bg-slate-500 text-white', cell: 'bg-slate-200 text-slate-600' },
};
const REASON_LABEL = {
  sick: 'Sick', family: 'Family', transport: 'Transport', work: 'Work', childcare: 'Childcare', no_reason: 'No reason given', other: 'Other',
};

const fmtDate = (iso, opts = { weekday: 'short', day: 'numeric', month: 'short' }) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString('en-GB', opts);

const pickDefaultSession = (sessions) => {
  const today = sessions.find(s => s.when === 'today');
  if (today) return today.date;
  const past = sessions.filter(s => s.when === 'past');
  if (past.length) return past[past.length - 1].date;
  return sessions[0]?.date || null;
};

const CourseRegister = ({ course, onBack, initialDate = null, onOpenStudent }) => {
  const { celebrate } = useCelebration();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [sendAbsence, setSendAbsence] = useState(true);
  const [onlyMissing, setOnlyMissing] = useState(false);
  const [messageTarget, setMessageTarget] = useState(null);
  const chipsRef = useRef(null);

  const token = localStorage.getItem('token');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchRegister = useCallback(async (keepDate) => {
    try {
      const res = await fetch(`${API_URL}/api/training/courses/${course.course_id}/register`, {
        headers: { 'Authorization': `Bearer ${token}` }, credentials: 'include',
      });
      if (!res.ok) { toast.error('Failed to load the register'); return; }
      const body = await res.json();
      setData(body);
      setSelectedDate(prev => {
        if (keepDate && prev) return prev;
        if (initialDate && body.sessions.some(s => s.date === initialDate)) return initialDate;
        return pickDefaultSession(body.sessions);
      });
    } finally {
      setLoading(false);
    }
  }, [course.course_id, token, initialDate]);

  useEffect(() => { fetchRegister(false); }, [fetchRegister]);
  useEffect(() => { setDraft({}); }, [selectedDate]);

  useEffect(() => {
    const el = chipsRef.current?.querySelector('[data-selected="true"]');
    el?.scrollIntoView({ block: 'nearest', inline: 'center' });
  }, [selectedDate, data]);

  const session = useMemo(() => data?.sessions.find(s => s.date === selectedDate), [data, selectedDate]);
  const saved = (data?.records || {})[selectedDate] || {};
  const current = (eid) => (eid in draft ? draft[eid] : saved[eid]) || null;
  const changes = Object.keys(draft).length;

  const setMark = (eid, patch) => {
    setDraft(prev => {
      const base = (eid in prev ? prev[eid] : saved[eid]) || {};
      return { ...prev, [eid]: { ...base, ...patch } };
    });
  };

  const toggleStatus = (eid, status) => {
    const cur = current(eid);
    if (cur?.status === status) {
      setMark(eid, { status: saved[eid] ? 'clear' : null, reason: null, note: null });
    } else {
      setMark(eid, { status, reason: status === 'present' ? null : cur?.reason || null });
    }
  };

  const markAllPresent = () => {
    const next = { ...draft };
    (data?.students || []).forEach(s => {
      if (!current(s.enrollment_id)?.status || current(s.enrollment_id)?.status === 'clear') {
        next[s.enrollment_id] = { status: 'present', reason: null, note: null };
      }
    });
    setDraft(next);
  };

  const newlyAbsent = Object.entries(draft).filter(([eid, m]) => m?.status === 'absent' && saved[eid]?.status !== 'absent').length;
  const canSendAbsence = data?.settings?.absence_message_enabled && session &&
    (Date.now() - new Date(`${session.date}T00:00:00`).getTime()) <= 2 * 86400000;

  const save = async () => {
    const records = Object.entries(draft)
      .filter(([, m]) => m && m.status)
      .map(([enrollment_id, m]) => ({ enrollment_id, status: m.status, reason: m.reason || null, note: m.note || null }));
    if (records.length === 0) { setDraft({}); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/training/attendance/mark`, {
        method: 'POST', headers, credentials: 'include',
        body: JSON.stringify({
          course_id: course.course_id, class_date: selectedDate, records,
          send_absence_messages: canSendAbsence && sendAbsence,
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (res.ok) {
        const unmarked = (data?.students || []).filter(s => {
          const m = records.find(r => r.enrollment_id === s.enrollment_id);
          return !(m ? m.status !== 'clear' : saved[s.enrollment_id]);
        }).length;
        if (unmarked === 0) celebrate({ count: 60 });
        toast.success(`Register saved${body.absence_messages_queued ? ` · messaging ${body.absence_messages_queued} absent student${body.absence_messages_queued > 1 ? 's' : ''}` : ''}${unmarked ? ` · ${unmarked} not marked yet` : ''}`);
        setDraft({});
        fetchRegister(true);
      } else {
        toast.error(body.detail || 'Failed to save the register');
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-[60vh]"><Loader2 className="w-8 h-8 text-pink-400 animate-spin" /></div>;
  }
  if (!data) return null;

  const { summary, students, sessions, messages } = data;
  const visibleSessions = onlyMissing ? sessions.filter(s => s.register_missing || s.date === selectedDate) : sessions;
  const heldSessions = sessions.filter(s => s.when !== 'upcoming');
  const sessionMsgs = (messages || {})[selectedDate] || {};

  const chipClass = (s) => {
    if (s.date === selectedDate) return 'border-pink-400 bg-pink-50 ring-2 ring-pink-200';
    if (s.register_missing) return 'border-amber-300 bg-amber-50';
    if (s.when === 'upcoming') return 'border-slate-200 bg-white opacity-70';
    return 'border-slate-200 bg-white';
  };

  return (
    <div className="space-y-5 animate-fade-in">
      <div className="flex items-start gap-3">
        <Button variant="ghost" size="sm" onClick={onBack} className="rounded-xl mt-1">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Register — {data.course.name}
          </h1>
          <p className="text-sm text-slate-500">
            {fmtDate(data.course.start_date, { day: 'numeric', month: 'short' })} – {fmtDate(data.course.end_date, { day: 'numeric', month: 'short', year: 'numeric' })}
            {data.course.location ? ` · ${data.course.location}` : ''}
          </p>
        </div>
      </div>

      <StatStrip items={[
        { label: 'Classes held', icon: CalendarDays, value: `${summary.sessions_held}/${summary.sessions_total}` },
        { label: 'Registers not taken', icon: AlertTriangle, value: summary.registers_missing, sub: summary.registers_missing ? 'past classes with no marks' : 'all up to date' },
        { label: 'Average attendance', icon: Percent, value: summary.average_attendance != null ? `${summary.average_attendance}%` : '—', sub: 'excused classes not counted' },
        { label: 'Students at risk', icon: Users, value: summary.at_risk, sub: 'below 80% or 2+ absences in a row' },
      ]} />

      <Tabs defaultValue="mark">
        <TabsList className="grid grid-cols-2 w-full sm:w-80">
          <TabsTrigger value="mark">Take register</TabsTrigger>
          <TabsTrigger value="history">History</TabsTrigger>
        </TabsList>

        <TabsContent value="mark" className="mt-4 space-y-4">
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm font-medium text-slate-700">Class dates</p>
            <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
              <input type="checkbox" checked={onlyMissing} onChange={(e) => setOnlyMissing(e.target.checked)} className="accent-pink-500" />
              Only registers not taken
            </label>
          </div>
          {sessions.length === 0 ? (
            <p className="text-sm text-slate-500">This course has no class dates. Check its dates and class days.</p>
          ) : (
            <div ref={chipsRef} className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1">
              {visibleSessions.map(s => (
                <button
                  key={s.date}
                  type="button"
                  data-selected={s.date === selectedDate}
                  onClick={() => {
                    if (changes && !window.confirm('You have unsaved marks. Leave this date without saving?')) return;
                    setSelectedDate(s.date);
                  }}
                  className={`flex-shrink-0 w-24 rounded-xl border px-2 py-2 text-left transition-all ${chipClass(s)}`}
                >
                  <p className="text-[11px] text-slate-500">{s.when === 'today' ? 'Today' : fmtDate(s.date, { weekday: 'short' })}</p>
                  <p className="text-sm font-semibold text-slate-900">{fmtDate(s.date, { day: 'numeric', month: 'short' })}</p>
                  <p className="text-[11px] text-slate-500">{s.start}</p>
                  <p className="text-[11px] mt-1 font-medium">
                    {s.when === 'upcoming' ? <span className="text-slate-400">Upcoming</span>
                      : s.register_missing ? <span className="text-amber-700">Not taken</span>
                      : <span className="text-green-700">{s.counts.present + s.counts.late}/{students.length} in</span>}
                  </p>
                </button>
              ))}
            </div>
          )}

          {session && session.when === 'upcoming' && (
            <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3">
              <div className="flex items-start gap-3">
                <CalendarClock className="w-5 h-5 text-slate-400 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-slate-900">{fmtDate(session.date, { weekday: 'long', day: 'numeric', month: 'long' })}, {session.start}–{session.end}</p>
                  <p className="text-sm text-slate-500">
                    {data.settings.class_reminder_enabled
                      ? `Reminders go out automatically at ${data.settings.reminder_hour}:00 the day before.`
                      : 'Class reminders are off. Turn them on in Courses → Messages.'}
                  </p>
                </div>
              </div>
              <div className="divide-y divide-slate-100">
                {students.map(st => {
                  const sent = (sessionMsgs[st.enrollment_id] || []).filter(m => m.kind === 'class_reminder');
                  return (
                    <div key={st.enrollment_id} className="flex items-center justify-between py-2 text-sm">
                      <span className="text-slate-800">{st.name}</span>
                      <span className="text-xs text-slate-500">
                        {sent.length ? sent.map(m => `${m.channel === 'sms' ? 'Text' : 'Email'} ${m.status}`).join(' · ') : 'No reminder yet'}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {session && session.when !== 'upcoming' && (
            <>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm text-slate-600">
                  <span className="font-semibold text-slate-900">{fmtDate(session.date, { weekday: 'long', day: 'numeric', month: 'long' })}</span>
                  {' '}· {session.start}–{session.end}{session.extra ? ' · extra class' : ''}
                </p>
                <Button variant="outline" size="sm" className="rounded-xl" onClick={markAllPresent} disabled={students.length === 0}>
                  <CheckCircle className="w-4 h-4 mr-1" /> Mark unmarked as present
                </Button>
              </div>

              {students.length === 0 ? (
                <p className="text-sm text-slate-500 py-6 text-center">No students enrolled on this course yet.</p>
              ) : (
                <div className="space-y-2">
                  {students.map(st => {
                    const mark = current(st.enrollment_id);
                    const status = mark?.status && mark.status !== 'clear' ? mark.status : null;
                    const dirty = st.enrollment_id in draft;
                    const msgs = sessionMsgs[st.enrollment_id] || [];
                    return (
                      <div key={st.enrollment_id} className={`rounded-2xl border bg-white p-3 sm:p-4 ${dirty ? 'border-pink-300' : 'border-slate-200'}`}>
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <button type="button" onClick={() => onOpenStudent?.(st.lead_id)} className="text-sm font-semibold text-slate-900 truncate hover:text-pink-600 text-left">{st.name}</button>
                              {st.at_risk && <Badge className="bg-red-100 text-red-700 text-[11px]">At risk</Badge>}
                              {!status && <span className="text-[11px] text-slate-400">Not marked</span>}
                            </div>
                            <p className="text-xs text-slate-500">
                              {st.stats.percentage != null ? `${st.stats.percentage}% attendance` : 'No classes counted yet'}
                              {st.stats.absent_streak >= 2 ? ` · absent ${st.stats.absent_streak} in a row` : ''}
                              {msgs.length ? ` · ${msgs.map(m => `${m.kind === 'absence' ? 'missed-you' : m.kind === 'class_reminder' ? 'reminder' : 'message'} ${m.channel} ${m.status}`).join(', ')}` : ''}
                            </p>
                          </div>
                          <div className="flex items-center gap-1">
                            {st.phone && (
                              <a href={`tel:${st.phone}`} aria-label={`Call ${st.name}`} className="w-9 h-9 rounded-xl bg-slate-100 text-slate-600 flex items-center justify-center hover:bg-slate-200">
                                <Phone className="w-4 h-4" />
                              </a>
                            )}
                            <button type="button" aria-label={`Text ${st.name}`} onClick={() => setMessageTarget({ student: st, channel: 'sms' })}
                              className="w-9 h-9 rounded-xl bg-slate-100 text-slate-600 flex items-center justify-center hover:bg-slate-200">
                              <MessageSquare className="w-4 h-4" />
                            </button>
                            <button type="button" aria-label={`Email ${st.name}`} onClick={() => setMessageTarget({ student: st, channel: 'email' })}
                              className="w-9 h-9 rounded-xl bg-slate-100 text-slate-600 flex items-center justify-center hover:bg-slate-200">
                              <Mail className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                        <div className="grid grid-cols-4 gap-2 mt-3">
                          {Object.entries(STATUS).map(([key, cfg]) => (
                            <button
                              key={key}
                              type="button"
                              aria-pressed={status === key}
                              onClick={() => toggleStatus(st.enrollment_id, key)}
                              className={`px-2 py-2 rounded-xl text-xs font-medium transition-all ${status === key ? cfg.on : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                            >
                              {cfg.label}
                            </button>
                          ))}
                        </div>
                        {status && status !== 'present' && (
                          <div className="grid grid-cols-1 sm:grid-cols-[12rem_1fr] gap-2 mt-2">
                            <Select value={mark?.reason || ''} onValueChange={(v) => setMark(st.enrollment_id, { reason: v })}>
                              <SelectTrigger className="rounded-xl h-9 text-xs" aria-label="Reason"><SelectValue placeholder="Reason" /></SelectTrigger>
                              <SelectContent>
                                {(data.absence_reasons || []).map(r => <SelectItem key={r} value={r}>{REASON_LABEL[r] || r}</SelectItem>)}
                              </SelectContent>
                            </Select>
                            <Input
                              value={mark?.note || ''}
                              onChange={(e) => setMark(st.enrollment_id, { note: e.target.value })}
                              placeholder="Note (optional)"
                              maxLength={300}
                              className="rounded-xl h-9 text-xs"
                              aria-label="Note"
                            />
                          </div>
                        )}
                        {status === 'absent' && !dirty && (
                          <button type="button" onClick={() => setMessageTarget({ student: st, channel: 'sms', kind: 'absence' })}
                            className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-pink-600 hover:text-pink-700">
                            <UserX className="w-3.5 h-3.5" /> Send "we missed you"
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {students.length > 0 && (
                <div className="sticky bottom-4 rounded-2xl bg-white/95 backdrop-blur border border-slate-200 shadow-lg p-3 flex flex-col sm:flex-row sm:items-center gap-3">
                  {canSendAbsence && newlyAbsent > 0 ? (
                    <label className="flex items-center gap-2 text-sm text-slate-700 flex-1 cursor-pointer">
                      <input type="checkbox" checked={sendAbsence} onChange={(e) => setSendAbsence(e.target.checked)} className="accent-pink-500" />
                      <BellRing className="w-4 h-4 text-pink-500" /> Send "we missed you" to {newlyAbsent} newly absent
                    </label>
                  ) : (
                    <p className="text-sm text-slate-500 flex-1">{changes ? `${changes} unsaved change${changes > 1 ? 's' : ''}` : 'Tap a status for each student, then save.'}</p>
                  )}
                  <Button className="rounded-xl bg-green-600 hover:bg-green-700 text-white h-11 px-6" onClick={save} disabled={saving || changes === 0}>
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                    Save register
                  </Button>
                </div>
              )}
            </>
          )}
        </TabsContent>

        <TabsContent value="history" className="mt-4">
          {heldSessions.length === 0 || students.length === 0 ? (
            <p className="text-sm text-slate-500 py-6 text-center">History appears once classes have been held.</p>
          ) : (
            <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
              <table className="text-xs border-collapse min-w-full">
                <thead>
                  <tr className="bg-slate-50">
                    <th className="sticky left-0 bg-slate-50 text-left font-semibold text-slate-700 px-3 py-2 min-w-[10rem]">Student</th>
                    <th className="px-2 py-2 font-semibold text-slate-700">%</th>
                    {heldSessions.map(s => (
                      <th key={s.date} className={`px-1.5 py-2 font-medium whitespace-nowrap ${s.register_missing ? 'text-amber-700 bg-amber-50' : 'text-slate-500'}`}
                        title={s.register_missing ? 'Register not taken' : undefined}>
                        {fmtDate(s.date, { day: 'numeric', month: 'short' })}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {students.map(st => (
                    <tr key={st.enrollment_id} className="border-t border-slate-100">
                      <td className="sticky left-0 bg-white px-3 py-1.5 font-medium text-slate-800 whitespace-nowrap">
                        <button type="button" onClick={() => onOpenStudent?.(st.lead_id)} className="hover:text-pink-600">{st.name}</button>{st.at_risk && <span className="ml-1 text-red-500" title="At risk">●</span>}
                      </td>
                      <td className={`px-2 py-1.5 text-center font-semibold ${st.stats.percentage != null && st.stats.percentage < 80 ? 'text-red-600' : 'text-slate-700'}`}>
                        {st.stats.percentage != null ? st.stats.percentage : '—'}
                      </td>
                      {heldSessions.map(s => {
                        const m = (data.records[s.date] || {})[st.enrollment_id];
                        const cfg = m ? STATUS[m.status] : null;
                        const tip = m ? [cfg?.label, m.reason ? REASON_LABEL[m.reason] : null, m.note].filter(Boolean).join(' · ') : 'Not marked';
                        return (
                          <td key={s.date} className="px-1 py-1 text-center">
                            <button type="button" title={tip} aria-label={`${st.name} ${s.date}: ${tip}`}
                              onClick={() => setSelectedDate(s.date)}
                              className={`w-7 h-7 rounded-md font-semibold ${cfg ? cfg.cell : 'bg-slate-50 text-slate-300'}`}>
                              {cfg ? cfg.short : '–'}
                            </button>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="text-xs text-slate-400 mt-2">P present · L late · A absent · E excused · – not marked. Hover a cell for the reason. Amber dates = register not taken.</p>
        </TabsContent>
      </Tabs>

      <MessageDialog
        open={!!messageTarget}
        onOpenChange={(o) => { if (!o) setMessageTarget(null); }}
        student={messageTarget?.student}
        defaultChannel={messageTarget?.channel}
        kind={messageTarget?.kind || 'manual'}
        courseId={course.course_id}
        classDate={selectedDate}
        onSent={() => fetchRegister(true)}
      />
    </div>
  );
};

export default CourseRegister;
